import React, { useState } from 'react';
import { Modal, Form, Input, Button, message, Alert, Space, Spin } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import { adminAPI } from '../services/api';

interface TelegramIdEditorProps {
  visible: boolean;
  adminId: number;
  currentTelegramId?: number | null;
  onClose: () => void;
  onSuccess: () => void;
}

const TelegramIdEditor: React.FC<TelegramIdEditorProps> = ({
  visible,
  adminId,
  currentTelegramId,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validation, setValidation] = useState<{
    valid?: boolean;
    user?: any;
    error?: string;
  } | null>(null);
  const [skipValidation, setSkipValidation] = useState(false);

  const handleValidate = async () => {
    const telegramId = form.getFieldValue('telegramId');

    if (!telegramId || !/^\d+$/.test(telegramId)) {
      message.error('请输入有效的 Telegram ID');
      return;
    }

    setValidating(true);
    setValidation(null);

    try {
      const response = await adminAPI.validateTelegramId(parseInt(telegramId));
      setValidation(response.data);

      if (response.data.valid) {
        message.success('验证成功！该 Telegram ID 存在');
      } else {
        message.warning(response.data.error);
      }
    } catch (error: any) {
      message.error('验证失败');
      setValidation({ valid: false, error: error.message });
    } finally {
      setValidating(false);
    }
  };

  const handleSubmit = async (values: { telegramId: string }) => {
    setLoading(true);

    try {
      await adminAPI.updateTelegramId(
        adminId,
        parseInt(values.telegramId),
        skipValidation
      );

      message.success('Telegram ID 更新成功');
      onSuccess();
      handleClose();
    } catch (error: any) {
      const errorMsg = error.response?.data?.error || '更新失败';
      message.error(errorMsg);

      // 如果提示可以跳过验证
      if (error.response?.data?.suggestion) {
        setValidation({
          valid: false,
          error: errorMsg,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    form.resetFields();
    setValidation(null);
    setSkipValidation(false);
    onClose();
  };

  return (
    <Modal
      title="修改 Telegram ID"
      open={visible}
      onCancel={handleClose}
      footer={null}
      width={500}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{ telegramId: currentTelegramId }}
      >
        <Alert
          message="提示"
          description="修改 Telegram ID 后，管理员将使用新的 ID 与机器人交互。建议先验证 ID 是否存在。"
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        {currentTelegramId && (
          <Alert
            message={`当前 Telegram ID: ${currentTelegramId}`}
            type="info"
            style={{ marginBottom: 16 }}
          />
        )}

        <Form.Item
          label="新的 Telegram ID"
          name="telegramId"
          rules={[
            { required: true, message: '请输入 Telegram ID' },
            { pattern: /^\d+$/, message: '必须是数字' },
          ]}
        >
          <Input
            placeholder="请输入 Telegram ID"
            suffix={
              <Button
                type="link"
                size="small"
                onClick={handleValidate}
                loading={validating}
              >
                验证
              </Button>
            }
          />
        </Form.Item>

        {validating && (
          <div style={{ marginBottom: 16, textAlign: 'center' }}>
            <Spin indicator={<LoadingOutlined />} />
            <span style={{ marginLeft: 8 }}>正在验证...</span>
          </div>
        )}

        {validation && !validating && (
          <Alert
            message={
              validation.valid ? (
                <Space>
                  <CheckCircleOutlined style={{ color: '#52c41a' }} />
                  <span>验证成功</span>
                </Space>
              ) : (
                <Space>
                  <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                  <span>验证失败</span>
                </Space>
              )
            }
            description={
              validation.valid ? (
                <div>
                  <p>用户信息：</p>
                  <p>• ID: {validation.user?.id}</p>
                  <p>• 名称: {validation.user?.first_name} {validation.user?.last_name || ''}</p>
                  {validation.user?.username && <p>• 用户名: @{validation.user.username}</p>}
                </div>
              ) : (
                <div>
                  <p>{validation.error}</p>
                  {!skipValidation && (
                    <Button
                      type="link"
                      size="small"
                      onClick={() => setSkipValidation(true)}
                      style={{ padding: 0 }}
                    >
                      跳过验证并继续
                    </Button>
                  )}
                  {skipValidation && (
                    <p style={{ color: '#faad14', marginTop: 8 }}>
                      ⚠️ 已启用跳过验证模式
                    </p>
                  )}
                </div>
              )
            }
            type={validation.valid ? 'success' : 'warning'}
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        <Form.Item>
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={handleClose}>取消</Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              确认修改
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default TelegramIdEditor;
